import { AuthenticatedRequestHandler, IAuthenticatedRequestHandlerResponse } from '@api/utils/AuthenticatedRequestHandler'
import AssociateNotificationClient from 'IhmAssociateNotificationService-coral-javascript-clients/jquery-client'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { getActiveRealm, getRealmForStore } from '@utils/RegionRealms'

/** Callers append a path straight onto the endpoint, and the configured endpoints are inconsistent about it. */
const withTrailingSlash = (endpoint: string): string => endpoint.endsWith('/') ? endpoint : endpoint + '/'

/**
 * AssociateNotificationClientModule
 * This module is responsible for calling IhmAssociateNotification APIs.
 *
 * A store's notifications live in the cell that owns the store, so every call here is addressed
 * from the *store's* realm rather than from the realm the session authenticated in — see
 * "Region and Cell Routing" in AGENTS.md. All North America Cognito pools are in IAD while stores
 * can be in either US cell, so a single endpoint fixed at construction meant an IAD-authenticated
 * user polling a PDX store's notifications asked IAD for them and got none.
 *
 * Endpoints are resolved per realm and cached. Realms the service is not deployed to fall back to
 * the session's own endpoint, which is today's behaviour: only prod publishes an endpoint for both
 * US realms, so in beta and gamma every realm resolves to the same one.
 */
export class AssociateNotificationServiceClient {
    private static _instance: AssociateNotificationServiceClient | undefined
    // TODO: create an interface for this, or use a typescript generator
    private readonly client: any
    private readonly sessionEndpoint: string
    private readonly endpointsByRealm = new Map<string, string>()

    public static getInstance (): AssociateNotificationServiceClient {
        if (!AssociateNotificationServiceClient._instance) {
            AssociateNotificationServiceClient._instance = new AssociateNotificationServiceClient()
        }
        return AssociateNotificationServiceClient._instance
    }

    /** @internal Drops the singleton and its cached endpoints — for testing only */
    public static _reset (): void {
        AssociateNotificationServiceClient._instance = undefined
    }

    public async getNotificationsFromAssociateNotification (storeId, user, zone, subzone): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.endpointForStore(storeId) + 'notification',
            method: 'GET',
            queryParams: {
                storeId,
                user,
                zone,
                subzone,
            }})
    }

    public async acknowledgeNotification (notificationId, userId, deviceId, storeId): Promise<IAuthenticatedRequestHandlerResponse> {
        return this.sendRequest({
            url: this.endpointForStore(storeId) + 'acknowledge',
            method: 'GET',
            queryParams: {
                notificationId,
                userId,
                deviceId,
                storeId,
            }})
    }

    /**
     * Private Constructor setting the AssociateNotification client and the endpoint for the cell
     * this session is on, which remains the fallback for stores whose cell is not yet known.
     * @private
     */
    private constructor () {
        this.client = AssociateNotificationClient
        this.client.config.overrideDomain(EnvironmentDetail.getDomain())
        this.client.config.overrideRealm(EnvironmentDetail.getRealm())
        this.sessionEndpoint = withTrailingSlash(this.client.config.endpoint())
        console.log('ATBase: AssociateNotificationClient session endpoint: ', this.sessionEndpoint)
    }

    /**
     * The endpoint for the cell owning the given store: its own cell when we know it, otherwise the
     * active store's cell, otherwise the session's. Cached per realm — resolution reads the client
     * config, and notifications are polled every few seconds.
     */
    private endpointForStore (storeId: string | undefined): string {
        const realm = getRealmForStore(storeId) ?? getActiveRealm()
        // getActiveRealm falls back to the pinned cell, so this is unreachable in practice; it is here
        // because the return type admits undefined, and a realm-less endpoint lookup would be a miss anyway.
        if (!realm) {
            return this.sessionEndpoint
        }
        let endpoint = this.endpointsByRealm.get(realm)
        if (!endpoint) {
            endpoint = this.resolveEndpointForRealm(realm)
            this.endpointsByRealm.set(realm, endpoint)
        }
        return endpoint
    }

    /**
     * Looks the realm's endpoint up in the client config, falling back to the session's endpoint.
     *
     * The config is a plain object keyed by domain then realm, and a realm the service is not
     * deployed to is present but empty — so a miss surfaces either as a throw or as a non-string,
     * and both have to be treated as "no endpoint here".
     */
    private resolveEndpointForRealm (realm: string): string {
        let endpoint
        try {
            endpoint = this.client.config.endpointForRealm(realm)
        } catch (error) {
            console.warn(`ATBase: AssociateNotificationClient could not resolve an endpoint for realm ${realm}`, error)
        }
        if (typeof endpoint !== 'string' || !endpoint) {
            console.warn(`ATBase: AssociateNotificationClient has no endpoint for realm ${realm}; using ${this.sessionEndpoint}`)
            return this.sessionEndpoint
        }
        return withTrailingSlash(endpoint)
    }

    /**
     * Makes the call to the abstract authenticatedRequestsClient.
     * @param options call parameters.
     * @returns {*} Output from the call (from the underlying service)
     */
    private async sendRequest (options): Promise<IAuthenticatedRequestHandlerResponse> {
        console.log('ATBase: sendRequest')
        const queryParamString = this.buildQueryParamString(options.queryParams)
        options.url = options.url + queryParamString
        delete options.queryParams
        return AuthenticatedRequestHandler.raw(options)
    }

    /**
     * Converts queryParam json object to queryParam string
     */
    private buildQueryParamString (queryParams: Record<string, string>): string {
        let queryParamString = '?'
        Object.keys(queryParams).forEach((key) => {
            queryParamString = queryParamString + key + '=' + queryParams[key] + '&'
        })
        return queryParamString.slice(0, -1)
    }

}
